import React, { useState, useRef, useEffect } from 'react';
import { Layout, Tour } from 'antd';
import { unstable_getServerSession } from 'next-auth/next';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import InstructionModal from '@src/components/instruction-modal';
import {
  GetUserSubscriptionDocument,
  SubscriptionPlansDocument,
} from '@src/components/generated/graphql';
import { useSceneLoaded } from '@src/components/hooks/useSceneLoaded';
import { openNotification } from 'util/helpers';
import { useDisableClicksOnSceneLoad } from '@src/components/hooks/useDisableClicksOnSceneLoad';
import { useWindowSize } from '@src/components/hooks/useWindowDimensions';
import Editor from '@src/components/scene/editor/Editor';
import { initializeApollo } from '@src/apolloClient';
import { useBranding } from '@src/theme/branding/useBranding';
import { isAllowedUserEditScene } from '@/components/user/scene-access/sceneEditAccess';
import { NavigationBar } from '@components/navigation-bar/NavigationBar';
import { getSkyboxList } from '@ozoneuniverse/mfe-core/dist/components/skyBox-manager';
import { getAssetPlan } from '@ozoneuniverse/mfe-core/dist/components/managers/AssetsPlan';
import { getSkyboxPreviews } from '@ozoneuniverse/mfe-core/dist/components/skyBox-manager';
import { lightsDefault } from '@ozoneuniverse/mfe-core/dist/components/lights-default';
import { CustomLoader } from '@src/components/scene/canvas-html/customLoader';
import type { TourProps } from 'antd';
import { useRouter } from 'next/router';

const client = initializeApollo();
const { Header, Content } = Layout;

export async function getServerSideProps(context) {
  const session = await unstable_getServerSession(
    context.req,
    context.res,
    authOptions
  );

  const isAllowed = await isAllowedUserEditScene({
    sceneId: context.query.id,
    apolloClient: client,
    accessorUserId: session?.user.id!,
  });

  if (!isAllowed) {
    return {
      redirect: {
        destination: '/',
        permanent: true,
      },
    };
  }

  const res = await client.query({
    query: GetUserSubscriptionDocument,
    variables: {
      where: {
        subscription: {
          some: {
            userId: {
              equals: session?.user.id,
            },
          },
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  console.log('res', res?.data?.subscriptionPlans?.[0]);

  console.log('session?.user', session?.user);

  // if (!res.data.subscriptionPlans[0]) {
  const plans = await client.query({
    query: SubscriptionPlansDocument,
    fetchPolicy: 'no-cache',
  });
  // }

  const user = {
    id: session?.user.id,
    email: session?.user.email,
    role: session?.user.role,
    phone: null,
    bio: null,
    name: null,
    lastname: null,
    walletId: null,
    alias: null,
  };

  const assetsPlan = getAssetPlan({ plans: ['Pack1'] });
  const buildSceneOut = Object.keys(assetsPlan)?.reduce((acc, planAct) => {
    const assetAct = assetsPlan[planAct];
    return {
      ...acc,
      [planAct]:
        assetAct?.map((item, index) => {
          const { name, url, preview } = item;
          return {
            name: `Build scene ${name}-${index}`,
            thumbnail: preview,
            url: url,
            nameObject: name,
          };
        }) ?? [],
    };
  }, {});

  const skyboxAux = [...getSkyboxList('default')];
  skyboxAux.shift();
  const skyboxPreviews = getSkyboxPreviews({ all: true });

  const skyboxOut = skyboxPreviews?.map((item: any, index) => {
    return {
      name: `Skybox scene-${index}`,
      thumbnail: item.url,
      object: {
        url: skyboxAux[index].baseUrl,
        fileName: skyboxAux[index].fileName,
        index: index,
      },
    };
  });
  const lightsOut = lightsDefault.map((item, index) => {
    return {
      name: `${item.label}-${index}`,
      thumbnail: item.thumbnail,
      lights: JSON.parse(
        JSON.stringify(
          item.lights.map((lightAct) => {
            return {
              ...lightAct,
              position: lightAct.position
                ? [
                    lightAct.position.x,
                    lightAct.position.y,
                    lightAct.position.z,
                  ]
                : [0, 0, 0],
            };
          })
        )
      ),
      index: index,
    };
  });

  return {
    props: {
      isSignIn: true,
      user,
      email: session?.user.email,
      userSubscription: res?.data?.subscriptionPlans?.[0] || [],
      plans: plans?.data?.subscriptionPlans,
      skyBoxIn: skyboxOut,
      buildScene: buildSceneOut,
      lightsIn: lightsOut,
    },
  };
}

export const DEFAULT_SCENE_TEMPLATE = -1;
export const REDIRECT_TO_STUDIO_EDITOR = 'redirect_to_studio_editor';

function Scene({ isSignIn, user, email, skyBoxIn, buildScene, lightsIn }) {
  const [isSaveScene, setIsSaveScene] = useState<boolean>(false);
  const { sceneFirstFullyLoaded } = useSceneLoaded();
  const size = useWindowSize();
  useDisableClicksOnSceneLoad();
  const [isExportScene, setIsExportScene] = useState<boolean | undefined>(
    undefined
  );
  const [exportSceneProgress, setExportSceneProgress] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [openTour, setOpenTour] = useState<boolean>(false);
  const { isZepeto, isUnity, isXsolla } = useBranding();

  const router = useRouter();
  const { id } = router.query;

  useEffect(() => {
    function redirectToStudioEditor() {
      if (!id) {
        openNotification(
          'Custom Code',
          'You need to save the scene first to access Custom Code'
        );
        return;
      }

      router.push(`/user/studio/code-editor/${id}`);
    }
    window.addEventListener(REDIRECT_TO_STUDIO_EDITOR, redirectToStudioEditor);

    return () => {
      window.removeEventListener(
        REDIRECT_TO_STUDIO_EDITOR,
        redirectToStudioEditor
      );
    };
  }, []);

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);
  const ref6 = useRef(null);
  const ref7 = useRef(null);
  const ref8 = useRef(null);
  const ref9 = useRef(null);
  const ref10 = useRef(null);
  const ref11 = useRef(null);
  const ref12 = useRef(null);
  const ref13 = useRef(null);
  const ref14 = useRef(null);
  const ref15 = useRef(null);
  const ref16 = useRef(null);
  const ref17 = useRef(null);
  const ref18 = useRef(null);

  const baseSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Add to scene',
      description: 'Tab with various objects that can be added to the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
    {
      title: 'Custom Code',
      description: 'Add custom code to your scene.',
      target: () => ref10.current,
    },
    {
      title: 'Sky',
      description: 'Configure sky and environment settings.',
      target: () => ref11.current,
    },
    {
      title: 'E-commerce',
      description: 'Manage e-commerce features and listings.',
      target: () => ref12.current,
    },
    {
      title: 'Web3',
      description: 'Access Web3 assets and NFT features.',
      target: () => ref13.current,
    },
    {
      title: 'OZONE AI Mind',
      description: 'AI-powered features and tools.',
      target: () => ref14.current,
    },
    {
      title: 'Game hub',
      description: 'Game development tools and features.',
      target: () => ref15.current,
    },
    {
      title: 'Networking',
      description: 'Enable text chat and networking features.',
      target: () => ref16.current,
    },
    {
      title: 'Graphics settings',
      description: 'Configure graphics and performance settings.',
      target: () => ref17.current,
    },
    {
      title: 'Admin',
      description: 'Administrative tools and features.',
      target: () => ref18.current,
    },
  ];

  const unitySteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'Export as Unity',
      description: 'Export your scene as Unity package and download.',
      target: () => ref4.current,
    },
    {
      title: 'My projects',
      description: 'Your projects and scenes.',
      target: () => ref5.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Asset store',
      description: 'Tab with 3D assets models for the scene.',
      target: () => ref7.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
    {
      title: 'Sky',
      description: 'Configure sky and environment settings.',
      target: () => ref11.current,
    },
  ];

  const zepetoSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'Export as ZepetoWeb',
      description: 'Export your scene as ZepetoWeb package and download.',
      target: () => ref4.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Quick Presets',
      description: 'Tab with World presets for the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
    {
      title: 'My Scenes',
      description: 'Your projects and scenes.',
      target: () => ref9.current,
    },
    {
      title: 'Asset store',
      description: 'Tab with 3D assets models for the scene.',
      target: () => ref7.current,
    },
    {
      title: 'Documentation',
      description: 'Access tutorials and documentation.',
      target: () => ref10.current,
    },
  ];

  const xsollaSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'My projects',
      description: 'Your projects and scenes.',
      target: () => ref9.current,
    },
    {
      title: 'Add to scene',
      description: 'Tab with various objects that can be added to the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
    {
      title: 'Custom Code',
      description: 'Add custom code to your scene.',
      target: () => ref10.current,
    },
    {
      title: 'Sky',
      description: 'Configure sky and environment settings.',
      target: () => ref11.current,
    },
    {
      title: '3D Gen AI',
      description: 'AI-powered 3D generation tools.',
      target: () => ref14.current,
    },
    {
      title: 'Game hub',
      description: 'Game development tools and features.',
      target: () => ref15.current,
    },
    {
      title: 'Enable text chat',
      description: 'Enable text chat and networking features.',
      target: () => ref16.current,
    },
    {
      title: 'Graphics settings',
      description: 'Configure graphics and performance settings.',
      target: () => ref17.current,
    },
  ];

  const steps: TourProps['steps'] = React.useMemo(() => {
    if (isZepeto) {
      return zepetoSteps;
    }

    if (isUnity) {
      return unitySteps;
    }

    if (isXsolla) {
      return xsollaSteps;
    }

    return baseSteps;
  }, [isZepeto, isUnity, isXsolla]);

  const finishTour = () => {
    setOpenTour(false);
    window.localStorage.setItem('not_show_tour', 'true');
  };

  useEffect(() => {
    if (window) {
      const notShowTour = window.localStorage.getItem('not_show_tour');

      if (notShowTour !== 'true' && sceneFirstFullyLoaded) {
        setOpenTour(true);
      }
    }
  }, [sceneFirstFullyLoaded]);

  useEffect(() => {
    const handleEditorHasChanges = (event: CustomEvent) => {
      setHasChanges(event.detail.hasChanges);
    };

    window.addEventListener(
      'editor_has_changes',
      handleEditorHasChanges as EventListener
    );

    return () => {
      window.removeEventListener(
        'editor_has_changes',
        handleEditorHasChanges as EventListener
      );
    };
  }, []);

  return (
    <>
      <Layout className='layout'>
        <style jsx global>{`
          #__next {
            height: 100%;
          }
          .ant-input-number-handler-wrap {
            opacity: 1;
          }
        `}</style>
        <InstructionModal />
        <Header className='header' style={{ minHeight: 62 }}>
          <NavigationBar
            userEmail={email}
            isSignIn={isSignIn}
            isCreator={false}
            setIsSaveScene={setIsSaveScene}
            setIsExportScene={setIsExportScene}
            isExportScene={isExportScene}
            exportSceneProgress={exportSceneProgress}
            hasChanges={hasChanges}
            ref1={ref1}
            ref2={ref2}
            ref3={ref3}
            ref4={ref4}
            ref5={ref5}
            ref6={ref6}
            ref7={ref7}
            ref8={ref8}
            ref9={ref9}
            ref10={ref10}
            ref11={ref11}
            ref12={ref12}
            ref13={ref13}
            ref14={ref14}
            ref15={ref15}
            ref16={ref16}
            ref17={ref17}
            ref18={ref18}
            setIsTourOpen={setOpenTour}
          />
        </Header>
        <Layout className='layout'>
          <CustomLoader />
          <Content className='content'>
            <Editor
              skyBoxIn={skyBoxIn}
              buildScene={buildScene}
              lightsIn={lightsIn}
              email={email}
              user={user}
              isSaveScene={isSaveScene}
              setIsSaveScene={setIsSaveScene}
              isExportScene={isExportScene}
              setIsExportScene={setIsExportScene}
              setExportSceneProgress={setExportSceneProgress}
              tourRefs={[
                ref5,
                ref6,
                ref7,
                ref8,
                null,
                ref5,
                ref6,
                ref7,
                ref8,
                ref9,
                ref10,
                ref11,
                ref12,
                ref13,
                ref14,
                ref15,
                ref16,
                ref17,
                ref18,
              ]}
              setIsTourOpen={setOpenTour}
            />
          </Content>
        </Layout>
        <Tour open={openTour} onClose={finishTour} steps={steps} />
      </Layout>
    </>
  );
}
export default Scene;
